/**
 * Formats a number with Indian currency format (2 decimal places)
 * @param {number} value - The number to format
 * @returns {string} - Formatted number string
 */
export const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  // Convert to number if string
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  // Format with Indian number system and 2 decimal places
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

/**
 * Formats a number as Indian currency (₹)
 * @param {number} value - The number to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}; 

/**
 * Rounds a number to exactly 2 decimal places using standard rounding rules
 * This is the main function to use for all calculations to ensure consistency
 * @param {number|string} value - The number to round
 * @returns {number} - Number rounded to 2 decimal places
 */
export const roundTo2Decimals = (value) => {
  if (!value && value !== 0) return 0;
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;
  
  // Use Math.round with Number.EPSILON to avoid floating point precision issues
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Rounds a number to exactly 2 decimal places and returns as string
 * @param {number|string} value - The number to round
 * @returns {string} - String representation with exactly 2 decimal places
 */
export const roundTo2DecimalsString = (value) => {
  return roundTo2Decimals(value).toFixed(2);
};

/**
 * Formats a percentage value to 2 decimal places
 * @param {number|string} value - The percentage value
 * @returns {string} - Formatted percentage with 2 decimal places
 */
export const formatPercentage = (value) => {
  const rounded = roundTo2Decimals(value);
  return `${rounded.toFixed(2)}%`;
};

/**
 * Calculates GST amounts with proper rounding to 2 decimal places
 * @param {number|string} amount - Base amount
 * @param {number|string} gstRate - GST rate percentage
 * @returns {number} - GST amount rounded to 2 decimal places
 */
export const calculateGSTAmount = (amount, gstRate) => {
  const baseAmount = roundTo2Decimals(amount);
  const rate = roundTo2Decimals(gstRate);
  return roundTo2Decimals((baseAmount * rate) / 100);
};

/**
 * Calculates line total with proper rounding
 * @param {number|string} qty - Quantity
 * @param {number|string} rate - Rate per unit
 * @param {number|string} lineDiscount - Line discount (optional)
 * @returns {number} - Total amount rounded to 2 decimal places
 */
export const calculateLineTotal = (qty, rate, lineDiscount = 0) => {
  const quantity = roundTo2Decimals(qty);
  const unitRate = roundTo2Decimals(rate);
  const discount = roundTo2Decimals(lineDiscount);
  
  const subtotal = roundTo2Decimals(quantity * unitRate);
  if (discount > 0) {
    const discountAmount = roundTo2Decimals((subtotal * discount) / 100);
    return roundTo2Decimals(subtotal - discountAmount);
  }
  return subtotal;
};

/**
 * Formats a date string according to company settings
 * @param {string|Date} dateStr
 * @param {Object} currentCompany - Company object with dateFormat preference
 * @returns {string}
 */
export const formatDate = (dateStr, currentCompany = null) => {
  // Import the centralized date formatting function
  const { formatDateToCompanyFormat } = require('./dateUtils');
  return formatDateToCompanyFormat(dateStr, currentCompany);
};

/**
 * Rounds a number to the nearest integer using 0.5 rule
 * @param {number|string} value - The number to round
 * @returns {number} - Rounded integer
 */
export const roundToNearestInteger = (value) => {
  if (!value && value !== 0) return 0;
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;
  
  return Math.round(num);
};

/**
 * Converts a number to words (Indian Rupees)
 * @param {number} num
 * @returns {string}
 */
export const numToWords = (num) => {
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ',
    'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if ((num = num.toString()).length > 9) return 'Overflow';
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] !== 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] !== 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] !== 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] !== 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
  return str.trim() + ' Rupees Only';
}; 